import {Fragment, useEffect, useState} from 'react';

import {Button} from 'primereact/button';
import {SelectButton} from 'primereact/selectbutton';
import {InputText} from 'primereact/inputtext';
import {Fieldset} from 'primereact/fieldset';
import {ConfirmDialogService, ToastService, useDataTable, useDialogCrup, useForm, useToolbar} from '@iamsoftware/react-hooks';

import {CustomerService as Service} from './CustomerService';
import {CommonService} from '../../../service/CommonService';

const statuses = [
  {value: 'N', label: 'Đang hoạt động'},
  {value: 'Y', label: 'Ngừng hoạt động'}
];

const partyTypes = [
  {value: 'PtyOrganization', label: 'Tổ chức'},
  {value: 'PtyPerson', label: 'Cá nhân'}
]

export const Customers = () => {

  const dataKey = 'partyId';
  const header = 'Danh sách khách hàng';

  const [statusId, setStatusId] = useState(statuses[0].value);
  const [readOnly, setReadOnly] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [counties, setCounties] = useState([]);
  const [partyTax, setPartyTax] = useState(null);
  const [lastSearchedTax, setLastSearchedTax] = useState<string | null>(null);
  const [partyName, setPartyName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [idValue, setIdValue] = useState('');

  useEffect(() => {
    CommonService.getProvinces().then(({listData}) => {
      setProvinces(listData.map(item => {
        return {value: item?.toGeoId, label: item?.geoName}
      }))
    })
  }, []);

  useEffect(() => {
    if (statusId) {
      reloadLazyData();
    }
  }, [statusId]);


  const tableHeader = (
    <div className="flex align-items-center justify-content-between">
      <span>{header}</span>
      <div>
        <Button label="Thêm" icon="pi pi-plus" severity="success" className="mr-1 mb-1 w-7rem h-2rem" size="small" onClick={() => doCreate()}/>
      </div>
    </div>
  )

  const {render: renderDataTable, reloadLazyData} = useDataTable({
    tableHeader: tableHeader,
    dataKey,
    indexColumnWidth: 45,
    columns: [
      {field: 'pseudoId', header: 'Mã khách hàng', width: 150, matchMode: 'contains'},
      {field: 'partyName', header: 'Tên khách hàng', minWidth: 250, matchMode: 'contains'},
      {field: 'address1', header: 'Địa chỉ', minWidth: 250, matchMode: 'contains'},
      {field: 'representativeName', header: 'Người đại diện', width: 150, matchMode: 'contains'},
      {field: 'emailAddress', header: 'Email', minWidth: 200, matchMode: 'contains'},
      {field: 'contactNumber', header: 'SĐT', width: 150, matchMode: 'contains'},
    ],
    actionColumnWidth: 120,
    getList(lazyLoadEvent: any): Promise<any> {
      return Service.getList(lazyLoadEvent, {disabled: statusId}).then()
    },
    initActions(items: Array<any>) {
      if (items) {
        items.forEach(item => {
          item.actions = [
            {icon: 'pi pi-pencil', title: 'Chỉnh sửa', className: 'p-button-warning', command: () => doUpdate(item, false)},
          ]
          if (item.disabled === 'Y') {
            item.actions.push({icon: 'pi pi-unlock', title: 'Mở khóa', className: 'p-button-success', command: () => doEnable(item)},)
          } else {
            item.actions.push({icon: 'pi pi-lock', title: 'Khóa', className: 'p-button-danger', command: () => doDisable(item)},)
          }
          item.actions.push({icon: 'pi pi-trash', title: 'Xóa', className: 'p-button-secondary', command: () => doDelete(item)},)
        })
      }
    }
  })

  const formRepresentative = useForm({
    fields: [
      {field: 'representativeName', header: 'Tên người đại diện', className: 'md:col-6'},
      {field: 'representativeJobTitle', header: 'Chức vụ người đại diện', className: 'md:col-6'}
    ],
    readOnly
  })

  const {render: renderDialogCrup, update, create, view, form} = useDialogCrup({
    header: 'khách hàng',
    dataKey,
    width: '80rem',
    fields: [
      {field: 'pseudoId', header: 'Mã khách hàng', required: true, className: 'md:col-3'},
      {field: 'partyTypeEnumId', header: 'Loại khách hàng', required: true, className: 'md:col-3', type: 'Dropdown', DropdownProps: {options: partyTypes}},
      {
        field: 'partyName', header: 'Tên khách hàng', required: true, className: 'md:col-6',
        type: 'custom',
        body: <Fragment>
          <label>Tên khách hàng</label>
          <InputText
            value={partyName}
            onChange={(e) => {
              const value = e.target.value.replace(/\d/g, '');
              setPartyName(value);
              if (form) {
                form.setValue({...form.getRawValue(), partyName: value});
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
              const cleaned = paste.replace(/\d/g, '');
              setPartyName(cleaned);
              if (form) {
                form.setValue({...form.getRawValue(), partyName: cleaned});
              }
            }}
            placeholder="Nhập tên khách hàng"
            disabled={readOnly}
          />
        </Fragment>
      },
      {
        field: 'partyTaxId', header: 'Mã số thuế', className: 'md:col-3', type: 'custom', body: <Fragment>
          <label>Mã số thuế</label>
          <div className="p-inputgroup flex-1">
            <InputText 
              value={partyTax || ''} 
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 13);
                e.target.value = value;
                setPartyTax(value);
              }}
              onPaste={(e) => {
                e.preventDefault();
                const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                const cleaned = paste.replace(/[^0-9]/g, '').slice(0, 13);
                setPartyTax(cleaned);
              }}
              maxLength={13}
              pattern="[0-9]*"
              inputMode="numeric"
              placeholder="Nhập mã số thuế"
            />
            <span className="p-inputgroup-addon">
                <Button icon="pi pi-search" className="p-button-info" size="small" onClick={() => doGetPartyByTax(partyTax)}/>
            </span>
          </div>
        </Fragment>,
      },
      {
        field: 'contactNumber', 
        header: 'Số điện thoại', 
        className: 'md:col-3',
        type: 'custom',
        body: <Fragment>
          <label>Số điện thoại</label>
          <InputText
            value={contactNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
              setContactNumber(value);
              if (form) {
                form.setValue({...form.getRawValue(), contactNumber: value});
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
              const cleaned = paste.replace(/[^0-9]/g, '').slice(0, 10);
              setContactNumber(cleaned);
              if (form) {
                form.setValue({...form.getRawValue(), contactNumber: cleaned});
              }
            }}
            maxLength={10}
            type="tel"
            placeholder="Nhập số điện thoại"
            disabled={readOnly}
          />
        </Fragment>
      },
      {field: 'bankName', header: 'Tên ngân hàng', className: 'md:col-3'},
      {field: 'accountNumber', header: 'Số tài khoản', className: 'md:col-3'},
      {
        field: 'idValue', header: 'Số CCCD/CMND', className: 'md:col-3', 
        disabledDependency(item: any): boolean {
          return item.partyTypeEnumId === 'PtyOrganization';
        },
        type: 'custom',
        body: (item: any) => <Fragment>
          <label>Số CCCD/CMND</label>
          <InputText
            value={idValue}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 12);
              setIdValue(value);
              if (form) {
                form.setValue({...form.getRawValue(), idValue: value});
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
              const cleaned = paste.replace(/[^0-9]/g, '').slice(0, 12);
              setIdValue(cleaned);
              if (form) {
                form.setValue({...form.getRawValue(), idValue: cleaned});
              }
            }}
            maxLength={12}
            placeholder="Nhập số CCCD/CMND"
            disabled={readOnly || item?.partyTypeEnumId === 'PtyOrganization'}
          />
        </Fragment>
      },
      {
        field: 'emailAddress', 
        header: 'Địa chỉ email', 
        className: 'md:col-3',
        InputTextProps: {
          type: 'email'
        }
      },
      {field: 'stateProvinceGeoId', header: 'Tỉnh/Thành phố', className: 'md:col-3', type: 'Dropdown', DropdownProps: {options: provinces, filter: true, filterBy: 'label', filterPlaceholder: 'Tìm kiếm tỉnh/thành phố'}},
      {field: 'countyGeoId', header: 'Phường/Xã', className: 'md:col-3', type: 'Dropdown', DropdownProps: {options: counties, filter: true, filterBy: 'label', filterPlaceholder: 'Tìm kiếm phường/xã'}},
      {field: 'address1', header: 'Địa chỉ', className: 'md:col-12'},
      {
        type: 'custom', className: 'md:col-12', displayDependency(item: any): boolean {
          return item.partyTypeEnumId === 'PtyOrganization'
        }, body: <Fieldset legend="Người đại diện">
          {formRepresentative.render()}
        </Fieldset>
      }
    ],
    async createItem(item: any): Promise<any> {
      const validationError = validateFormData(item);
      if (validationError) {
        ToastService.error(validationError);
        return Promise.reject(validationError);
      }

      const resp = await Service.create(inject(item));

      if (resp && resp.success === 'Y') {
        ToastService.success();
        reloadLazyData();
        return resp;
      } else {
        const msg = resp?.error || resp?.message || 'Tạo khách hàng thất bại';
        ToastService.error(msg);
        return Promise.reject(msg);
      }
    },
    async updateItem(id: string, item: any): Promise<any> {
      const validationError = validateFormData(item);
      if (validationError) {
        ToastService.error(validationError);
        return Promise.reject(validationError);
      }
      const resp = await Service.update(id, inject(item));
      if (resp && resp.success === 'Y') {
        ToastService.success();
        reloadLazyData();
        return resp;
      } else {
        const msg = resp?.error || resp?.message || 'Cập nhật khách hàng thất bại';
        ToastService.error(msg);
        return Promise.reject(msg);
      }
    },
    reloadLazyData,
  })

  const {stateProvinceGeoId} = form.getRawValue()

  //Lấy ra quận huyện khi đổi thành phố
  useEffect(() => {
    if (stateProvinceGeoId) {
      CommonService.getCounties(stateProvinceGeoId).then(({listData}) => {
        setCounties(listData.map(item => {
          return {value: item?.toGeoId, label: item?.geoName}
        }))
      })
    }
  }, [stateProvinceGeoId]);

  const _valueFormRep = formRepresentative.getRawValue()
  const _fromValue = form.getRawValue()

  const validateFormData = (item: any): string | null => {
    // Validate email
    if (item.emailAddress) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(item.emailAddress)) {
        return 'Địa chỉ email không hợp lệ';
      }
    }

    // Validate phone
    if (contactNumber) {
      const trimmedContactNumber = contactNumber?.trim() || '';
      if (trimmedContactNumber.length > 0 && trimmedContactNumber.length !== 10) {
        return 'Số điện thoại phải có đúng 10 số';
      }
    }

    // Validate tax ID
    if (partyTax) {
      const trimmedTaxCode = String(partyTax || '').trim();
      if (trimmedTaxCode.length > 0 && ![10, 12, 13].includes(trimmedTaxCode.length)) {
        return 'Mã số thuế phải có 10, 12 hoặc 13 số';
      }
    }

    // Validate CCCD/CMND
    if (idValue) {
      const trimmedIdentityCard = idValue?.trim() || '';
      if (trimmedIdentityCard.length > 0 && ![9, 12].includes(trimmedIdentityCard.length)) {
        return 'CCCD/CMND phải có 9 hoặc 12 số';
      }
    }

    return null;
  }

  const inject = (item) => {
    item = {...item, ..._valueFormRep}
    item.partyTaxId = partyTax;
    item.partyName = partyName || item.partyName;
    item.contactNumber = contactNumber || item.contactNumber;
    item.idValue = idValue || item.idValue;
    return item
  }

  const doGetPartyByTax = (partyTaxId) => {
    if (!partyTaxId || !partyTaxId.trim()) {
      ToastService.error('Vui lòng nhập mã số thuế');
      return;
    }

    const clearData = () => {
      setPartyName('');
      setContactNumber('');
      setIdValue('');
      setCounties([]);
      formRepresentative.reset();
      const clearedValue = {..._fromValue};
      clearedValue.pseudoId = '';
      clearedValue.partyName = '';
      clearedValue.contactNumber = '';
      clearedValue.idValue = '';
      clearedValue.emailAddress = '';
      clearedValue.stateProvinceGeoId = null;
      clearedValue.countyGeoId = null;
      clearedValue.address1 = '';
      clearedValue.bankName = '';
      clearedValue.accountNumber = '';
      clearedValue.representativeName = '';
      clearedValue.representativeJobTitle = '';
      form.setValue(clearedValue);
    };

    CommonService.getPartyByTax(partyTaxId).then((response) => {
      console.log(response);
      const data = response.data;
      
      // Kiểm tra nếu response có success = 'N' hoặc có error
      if (data?.errors ) {
        const errorMsg =  'Không tìm thấy thông tin khách hàng với mã số thuế này';
        ToastService.error(errorMsg);
        clearData();
        return;
      }
      
      // Kiểm tra nếu không tìm thấy dữ liệu
      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        ToastService.error('Không tìm thấy thông tin khách hàng với mã số thuế này');
        clearData();
        return;
      }

      const isEmpty = (value) => {
        return value === null || value === undefined || value === '' || String(value).trim() === '';
      };
      const newValue = {..._fromValue, ...data, ..._valueFormRep};
      const currentPseudoId = newValue.pseudoId ? String(newValue.pseudoId).trim() : '';
      const shouldUpdatePseudoId = isEmpty(currentPseudoId) || 
        (lastSearchedTax && currentPseudoId === lastSearchedTax);
      
      if (shouldUpdatePseudoId) {
        newValue.pseudoId = partyTaxId;
      }
      
      setLastSearchedTax(partyTaxId);
      formRepresentative.setValue(newValue);
      form.setValue(newValue);
      if (data.partyName) setPartyName(data.partyName);
      if (data.contactNumber) setContactNumber(data.contactNumber);
      if (data.idValue) setIdValue(data.idValue);
    }).catch((error) => {
      console.log(error);
      ToastService.error(`Không thể tìm kiếm thông tin khách hàng với MST : ${partyTaxId}`);
      clearData();
    })
  }

  const doCreate = () => {
    setReadOnly(false);
    formRepresentative.reset();
    setPartyTax(null);
    setPartyName('');
    setContactNumber('');
    setIdValue('');
    create({partyTypeEnumId: 'PtyOrganization'})
  }

  const doUpdate = (item: any, doView: boolean) => {
    if (item[dataKey]) {
      Service.get(item[dataKey]).then(data => {
        formRepresentative.setValue(data);
        setPartyTax(data.partyTaxId || null);
        setPartyName(data.partyName || '');
        setContactNumber(data.contactNumber || '');
        setIdValue(data.idValue || '');
        if (doView === true) {
          setReadOnly(true)
          view(data)
        } else {
          update(data);
        }
      })
    }
  }

  const doDisable = (item) => {
    if (item[dataKey]) {
      ConfirmDialogService.confirm('Khóa khách hàng', 'Bạn xác nhận muốn khóa khách hàng này?', () => {
        Service.disable(item[dataKey]).then((resp) => {
          if (resp && resp.success === 'N') {
            const msg = resp?.error || resp?.message || 'Khóa khách hàng thất bại';
            ToastService.error(msg);
          } else {
            ToastService.success();
            reloadLazyData();
          }
        }).catch((e) => {
          ToastService.error(e?.message || 'Khóa khách hàng thất bại');
        });
      })
    }
  }

  const doEnable = (item) => {
    if (item[dataKey]) {
      ConfirmDialogService.confirm('Mở khóa khách hàng', 'Bạn xác nhận mở khóa khách hàng này?', () => {
        Service.enable(item[dataKey]).then((resp) => {
          if (resp && resp.success === 'N') {
            const msg = resp?.error || resp?.message || 'Mở khóa khách hàng thất bại';
            ToastService.error(msg);
          } else {
            ToastService.success();
            reloadLazyData();
          }
        }).catch((e) => {
          ToastService.error(e?.message || 'Mở khóa khách hàng thất bại');
        });
      })
    }
  }

  const doDelete = (item) => {
    if (item[dataKey]) {
      ConfirmDialogService.confirm('Xóa khách hàng', 'Bạn xác nhận xóa khách hàng này?', () => {
        Service.delete(item[dataKey]).then((resp) => {
          if (resp && resp.success === 'N') {
            const msg = resp?.error || resp?.message || 'Xóa khách hàng thất bại';
            ToastService.error(msg);
          } else {
            ToastService.success();
            reloadLazyData();
          }
        }).catch((e) => {
          ToastService.error(e?.message || 'Xóa khách hàng thất bại');
        });
      })
    }
  }


  return (
    <div className="grid">
      <div className="col-12 pb-0">
        <SelectButton value={statusId} options={statuses} onChange={e => setStatusId(e.value)}/>
      </div>
      <div className="col-12 py-0" style={{maxHeight: 'calc(100vh - 12rem)'}}>
        {renderDataTable()}
      </div>

      {renderDialogCrup()}
    </div>
  );
}